import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Cancel, Save } from '@mui/icons-material';
import {
    REEL_MAX_DIMENSION,
    REEL_MIN_DIMENSION,
    REEL_SIZE_PRESETS,
    Reel,
    ReelUpsertPayload,
} from './ReelMaker.types';

export interface ReelDialogProps {
    open: boolean;
    /** When set, the dialog edits this reel; otherwise it creates a new one. */
    reel?: Reel | null;
    saving?: boolean;
    /** Server-side error to surface inside the dialog (e.g. duplicate name). */
    error?: string | null;
    onClose: () => void;
    onSave: (payload: ReelUpsertPayload) => void | Promise<void>;
}

const NAME_MAX = 120;
const DESCRIPTION_MAX = 2000;

const toInt = (raw: string): number | null => {
    const t = raw.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : NaN;
};

const dimensionError = (raw: string, label: string): string => {
    const n = toInt(raw);
    if (n === null) return `${label} is required`;
    if (Number.isNaN(n) || !Number.isInteger(n)) return `${label} must be a whole number of pixels`;
    if (n < REEL_MIN_DIMENSION || n > REEL_MAX_DIMENSION) {
        return `${label} must be between ${REEL_MIN_DIMENSION} and ${REEL_MAX_DIMENSION} px`;
    }
    return '';
};

/** New / Edit reel dialog: Name, Description, Width, Height (+ size presets). */
export const ReelDialog: React.FC<ReelDialogProps> = ({ open, reel, saving = false, error, onClose, onSave }) => {
    const isEditing = Boolean(reel);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(reel?.name ?? '');
        setDescription(reel?.description ?? '');
        setWidth(reel ? String(reel.width) : '');
        setHeight(reel ? String(reel.height) : '');
        setTouched(false);
    }, [open, reel]);

    const nameError = useMemo(() => {
        const t = name.trim();
        if (!t) return 'Name is required';
        if (t.length > NAME_MAX) return `Name must be ${NAME_MAX} characters or fewer`;
        return '';
    }, [name]);
    const descriptionError = description.length > DESCRIPTION_MAX ? `Description must be ${DESCRIPTION_MAX} characters or fewer` : '';
    const widthError = dimensionError(width, 'Width');
    const heightError = dimensionError(height, 'Height');
    const hasErrors = Boolean(nameError || descriptionError || widthError || heightError);

    const presetValue = useMemo(() => {
        const w = toInt(width);
        const h = toInt(height);
        const match = REEL_SIZE_PRESETS.find((p) => p.width === w && p.height === h);
        return match ? `${match.width}x${match.height}` : 'custom';
    }, [width, height]);

    const applyPreset = (value: string) => {
        if (value === 'custom') return;
        const [w, h] = value.split('x');
        setWidth(w);
        setHeight(h);
    };

    const handleSave = () => {
        setTouched(true);
        if (hasErrors || saving) return;
        void onSave({
            name: name.trim(),
            description: description.trim(),
            width: Number(width),
            height: Number(height),
        });
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEditing ? 'Edit Reel' : 'New Reel'}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Name"
                        size="small"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => setTouched(true)}
                        required
                        fullWidth
                        autoFocus
                        error={touched && Boolean(nameError)}
                        helperText={touched && nameError ? nameError : `${name.trim().length}/${NAME_MAX}`}
                        inputProps={{ maxLength: NAME_MAX }}
                    />
                    <TextField
                        label="Description"
                        size="small"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        error={Boolean(descriptionError)}
                        helperText={descriptionError || `${description.length}/${DESCRIPTION_MAX}`}
                        inputProps={{ maxLength: DESCRIPTION_MAX }}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>Size preset</InputLabel>
                        <Select
                            label="Size preset"
                            value={presetValue}
                            onChange={(e) => applyPreset(String(e.target.value))}
                        >
                            <MenuItem value="custom">Custom</MenuItem>
                            {REEL_SIZE_PRESETS.map((p) => (
                                <MenuItem key={p.label} value={`${p.width}x${p.height}`}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Width (px)"
                            size="small"
                            type="number"
                            value={width}
                            onChange={(e) => setWidth(e.target.value)}
                            onBlur={() => setTouched(true)}
                            required
                            fullWidth
                            error={touched && Boolean(widthError)}
                            helperText={touched && widthError ? widthError : ' '}
                            inputProps={{ min: REEL_MIN_DIMENSION, max: REEL_MAX_DIMENSION, step: 1 }}
                        />
                        <TextField
                            label="Height (px)"
                            size="small"
                            type="number"
                            value={height}
                            onChange={(e) => setHeight(e.target.value)}
                            onBlur={() => setTouched(true)}
                            required
                            fullWidth
                            error={touched && Boolean(heightError)}
                            helperText={touched && heightError ? heightError : ' '}
                            inputProps={{ min: REEL_MIN_DIMENSION, max: REEL_MAX_DIMENSION, step: 1 }}
                        />
                    </Box>
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose} startIcon={<Cancel />} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                    variant="contained"
                    color="success"
                    disabled={saving || (touched && hasErrors)}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReelDialog;
